import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  CreateSchoolAuthorityReq,
  EditSchoolAuthorityReq,
  SchoolAuthorityDetailExtended,
  SchoolAuthorityPermission,
  SchoolBasicInfo,
  SchoolContactDetail,
  UserSchoolAuthorityItem,
  IdRes,
  StatusRes,
  UpdateAboutUsReq,
  UpdateSchoolAuthorityBasicInfoReq,
  UpdateSchoolAuthorityContactReq,
  UpdateSchoolAuthorityMembershipsReq,
  UpdateSchoolAuthorityInfoReq,
  ReorderGalleryReq,
} from '../types/rest-api';

/**
 * Service for School Authority REST API endpoints.
 * Maps to OpenAPI tag: School Authority
 * Base path: /v1/school-authorities
 */
@Injectable({
  providedIn: 'root',
})
export class SchoolAuthorityApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/v1/school-authorities`;

  // POST /v1/school-authorities/create
  create(body: CreateSchoolAuthorityReq) {
    return this.http.post<IdRes>(`${this.baseUrl}/create`, body);
  }

  // GET /v1/school-authorities/my-school-authorities
  getMySchoolAuthorities() {
    return this.http.get<UserSchoolAuthorityItem[]>(
      `${this.baseUrl}/my-school-authorities`
    );
  }

  // GET /v1/school-authorities/{school_authority_id}
  getById(id: string) {
    return this.http.get<SchoolAuthorityDetailExtended>(
      `${this.baseUrl}/${id}`
    );
  }

  // PUT /v1/school-authorities/{school_authority_id}/update
  update(id: string, body: EditSchoolAuthorityReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${id}/update`, body);
  }

  // PUT /v1/school-authorities/{school_authority_id}/info
  updateInfo(id: string, body: UpdateSchoolAuthorityInfoReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${id}/info`, body);
  }

  // POST /v1/school-authorities/{school_authority_id}/profile-picture
  uploadProfilePicture(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${id}/profile-picture`,
      formData
    );
  }

  // DELETE /v1/school-authorities/{school_authority_id}/profile-picture
  deleteProfilePicture(id: string) {
    return this.http.delete<StatusRes>(`${this.baseUrl}/${id}/profile-picture`);
  }

  // POST /v1/school-authorities/{school_authority_id}/wall-picture
  uploadWallPicture(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${id}/wall-picture`,
      formData
    );
  }

  // DELETE /v1/school-authorities/{school_authority_id}/wall-picture
  deleteWallPicture(id: string) {
    return this.http.delete<StatusRes>(`${this.baseUrl}/${id}/wall-picture`);
  }

  // PUT /v1/school-authorities/{school_authority_id}/basic-info
  updateBasicInfo(id: string, body: UpdateSchoolAuthorityBasicInfoReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${id}/basic-info`, body);
  }

  // PUT /v1/school-authorities/{school_authority_id}/about-us
  updateAboutUs(id: string, body: UpdateAboutUsReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${id}/about-us`, body);
  }

  // PUT /v1/school-authorities/{school_authority_id}/what-we-offer
  updateWhatWeOffer(
    id: string,
    body: import('../types/rest-api').UpdateWhatWeOfferReq
  ) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${id}/what-we-offer`,
      body
    );
  }

  // PUT /v1/school-authorities/{school_authority_id}/educational-focus
  updateEducationalFocus(
    id: string,
    body: import('../types/rest-api').UpdateEducationalFocusReq
  ) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${id}/educational-focus`,
      body
    );
  }

  // PUT /v1/school-authorities/contacts
  updateContacts(body: UpdateSchoolAuthorityContactReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/contacts`, body);
  }

  // PUT /v1/school-authorities/{school_authority_id}/publish
  publish(id: string) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${id}/publish`, {});
  }

  // POST /v1/school-authorities/{school_authority_id}/video/upload-token
  requestVideoUploadToken(id: string) {
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${id}/video/upload-token`,
      {}
    );
  }

  // POST /v1/school-authorities/{school_authority_id}/video/{api_video_id}
  uploadVideo(id: string, apiVideoId: string) {
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${id}/video/${apiVideoId}`,
      {}
    );
  }

  // DELETE /v1/school-authorities/{school_authority_id}/video
  deleteVideo(id: string) {
    return this.http.delete<StatusRes>(`${this.baseUrl}/${id}/video`);
  }

  // POST /v1/school-authorities/{school_authority_id}/gallery
  uploadGalleryItem(
    id: string,
    fileImage: File,
    opts?: { fileAudio?: File; title?: string; description?: string }
  ) {
    const formData = new FormData();
    formData.append('file_image', fileImage);
    if (opts?.fileAudio) formData.append('file_audio', opts.fileAudio);
    if (opts?.title) formData.append('title', opts.title);
    if (opts?.description) formData.append('description', opts.description);
    return this.http.post<IdRes>(`${this.baseUrl}/${id}/gallery`, formData);
  }

  // PUT /v1/school-authorities/{school_authority_id}/gallery/{gallery_id}
  updateGalleryItem(
    id: string,
    galleryId: string,
    body: import('../types/rest-api').UpdateGalleryItemReq
  ) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${id}/gallery/${galleryId}`,
      body
    );
  }

  // DELETE /v1/school-authorities/{school_authority_id}/gallery/{gallery_id}
  deleteGalleryItem(id: string, galleryId: string) {
    return this.http.delete<StatusRes>(
      `${this.baseUrl}/${id}/gallery/${galleryId}`
    );
  }

  // PUT /v1/school-authorities/{id}/gallery/reorder
  reorderGallery(id: string, body: ReorderGalleryReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${id}/gallery/reorder`,
      body
    );
  }

  // POST /v1/school-authorities/{school_authority_id}/docs
  uploadDoc(id: string, file: File, title: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    return this.http.post<IdRes>(`${this.baseUrl}/${id}/docs`, formData);
  }

  // PUT /v1/school-authorities/{school_authority_id}/docs/{doc_id}
  updateDocTitle(
    id: string,
    docId: string,
    body: import('../types/rest-api').UpdateDocTitleReq
  ) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${id}/docs/${docId}`,
      body
    );
  }

  // DELETE /v1/school-authorities/{school_authority_id}/docs/{doc_id}
  deleteDoc(id: string, docId: string) {
    return this.http.delete<StatusRes>(`${this.baseUrl}/${id}/docs/${docId}`);
  }

  // PUT /v1/school-authorities/memberships
  updateMemberships(body: UpdateSchoolAuthorityMembershipsReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/memberships`, body);
  }

  // DELETE /v1/school-authorities/{school_authority_id}/members/{user_id}
  removeAuthorityMember(authorityId: string, userId: string) {
    return this.http.delete<StatusRes>(
      `${this.baseUrl}/${authorityId}/members/${userId}`
    );
  }

  // GET /v1/school-authorities/{school_authority_id}/schools
  getSchools(id: string) {
    return this.http.get<SchoolBasicInfo[]>(`${this.baseUrl}/${id}/schools`);
  }

  // GET /v1/school-authorities/{school_authority_id}/contacts
  getContacts(authorityId: string) {
    return this.http.get<SchoolContactDetail[]>(
      `${this.baseUrl}/${authorityId}/contacts`
    );
  }

  // GET /v1/school-authorities/{school_authority_id}/permissions
  getPermissions(id: string) {
    return this.http.get<SchoolAuthorityPermission>(
      `${this.baseUrl}/${id}/permissions`
    );
  }
}
